import {
  ChartPie,
  Fire,
  GlobeHemisphereWest,
  Link,
  LinkedinLogo,
  SealCheck,
  SlidersHorizontal,
} from "@phosphor-icons/react";

import type { WorkspaceAccount } from "common/types";

import AccountProxyTag from "./components/AccountTags/AccountProxyTag";
import AccountSsiTag from "./components/AccountTags/AccountSsiTag";
import LicenseTag from "./components/AccountTags/LicenseTag";
import LoginStatusTag from "./components/AccountTags/LoginStatusTag";
import WarmupState from "./components/AccountTags/WarmupState";
import AssignSeats from "./components/ManageAccounts/AssignSeats";
import ManageInteractions from "./components/ManageAccounts/ManageInteractions";
import UpdateProxies from "./components/ManageAccounts/UpdateProxies";

import type { JSX } from "react";
import type { AccountSearchParams } from "./types";

type FiltersType<Keys extends keyof AccountSearchParams> = {
  [K in Keys]: {
    icon: JSX.Element;
    label: string;
    options: Record<AccountSearchParams[K][number], string>;
  };
};

export const ACCOUNTS_FILTERS: FiltersType<keyof AccountSearchParams> = {
  state: {
    icon: <Link weight="bold" />,
    label: "Login status",
    options: {
      LoggedIn: "Connected",
      LoggedOut: "Disconnected",
    },
  },
  seat: {
    icon: <SealCheck weight="fill" />,
    label: "Seat",
    options: {
      outreach: "Outreach",
      rented: "Outreach Avatar",
      "no-seat": "No seat",
    },
  },
  linkedinSubscription: {
    icon: <LinkedinLogo weight="fill" />,
    label: "LinkedIn plan",
    options: {
      "sales-navigator": "Sales Navigator",
      free: "Free",
      premium: "Premium",
    },
  },
};

export const MANAGE_ACCOUNTS_ACTIONS = {
  proxy: {
    icon: GlobeHemisphereWest,
    label: "Change proxies",
    component: UpdateProxies,
  },
  interactions: {
    icon: SlidersHorizontal,
    label: "Manage interactions",
    component: ManageInteractions,
  },
  seats: { icon: SealCheck, label: "Assign seats", component: AssignSeats },
} as const;

export const MOBILE_TAGS = {
  loginStatus: {
    icon: <Link weight="bold" />,
    label: "Login status",
    renderTag: (account: WorkspaceAccount) => (
      <LoginStatusTag account={account} size="sm" />
    ),
    isDefault: true,
  },
  proxy: {
    icon: <GlobeHemisphereWest weight="fill" />,
    label: "Proxy",
    renderTag: (account: WorkspaceAccount) => (
      <AccountProxyTag account={account} />
    ),
    isDefault: true,
  },
  license: {
    icon: <SealCheck weight="fill" />,
    label: "Seat",
    renderTag: (account: WorkspaceAccount) => (
      <LicenseTag
        isRented={account.type === "rental"}
        license={account.license}
        size="sm"
      />
    ),
    isDefault: true,
  },
  warmup: {
    icon: <Fire weight="fill" />,
    label: "Warmup",
    renderTag: (account: WorkspaceAccount) => (
      <WarmupState accountId={account.id} isTag />
    ),
  },
  ssi: {
    icon: <ChartPie weight="fill" />,
    label: "SSI score",
    renderTag: (account: WorkspaceAccount) => (
      <AccountSsiTag account={account} isTag />
    ),
  },
} as const;

export const SSI_ITEMS = {
  PROFESSIONAL_BRAND: {
    label: "Establish your professional brand",
    bgClassName: "bg-yellow-400",
    textClassName: "text-yellow-600",
    fillClassName: "fill-yellow-400",
  },
  FIND_RIGHT_PEOPLE: {
    label: "Find the right people",
    bgClassName: "bg-purple-500",
    textClassName: "text-purple-600",
    fillClassName: "fill-purple-500",
  },
  INSIGHT_ENGAGEMENT: {
    label: "Engage with insights",
    bgClassName: "bg-orange-500",
    textClassName: "text-orange-600",
    fillClassName: "fill-orange-400",
  },
  STRONG_RELATIONSHIP: {
    label: "Build relationships",
    bgClassName: "bg-blue-400",
    textClassName: "text-blue-600",
    fillClassName: "fill-blue-400",
  },
} as const;
